import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  ExpandableText,
  Text,
} from "@/components/ui";
import { MatterSettlementNegotiationContextMenu } from "@/components/features/matter/context-menus/MatterSettlementNegotiationContextMenu";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { useUser } from "@/components/UserProvider";
import { type SettlementViewModel } from "@/hooks/matter-view-models/use-settlements-view-model";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterSlideoverInitialTabsType } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import dayjs from "dayjs";
import { Pencil } from "lucide-react";
import pluralize from "pluralize";

interface NegotiationCardProps {
  negotiation: SettlementViewModel;
  onEdit: (initialTab?: MatterSlideoverInitialTabsType) => void;
  matter: Matter;
}

export const NegotiationCard = ({
  negotiation,
  onEdit,
  matter,
}: NegotiationCardProps) => {
  const { hasActionPermission } = useUser();
  const permissionKey =
    negotiation.type === "Demand"
      ? ACTION_PERMISSION_KEYS.matterSettlementDemand
      : negotiation.type === "Offer"
        ? ACTION_PERMISSION_KEYS.matterSettlementOffer
        : ACTION_PERMISSION_KEYS.matterSettlementCounter;
  const canOpenSettlementSlideover =
    hasActionPermission(permissionKey, ACTION_PERMISSION_VALUES.update) ||
    hasActionPermission(permissionKey, ACTION_PERMISSION_VALUES.delete);
  const isPolicyLimit =
    negotiation.type === "Offer"
      ? !!negotiation.policyLimit
      : negotiation.type === "Counter"
        ? !!negotiation.policyLimit
        : !!negotiation.policyLimit;
  const acceptedStatus =
    negotiation.type === "Offer"
      ? negotiation.status
      : negotiation.type === "Counter"
        ? negotiation.status
        : undefined;
  const hasIssues =
    negotiation.type !== "Counter" &&
    negotiation.accepted &&
    negotiation.acceptedWithIssues &&
    ["Bodily Injury", "Property Damage", "UM", "UIM"].includes(
      negotiation.coverageType ?? "",
    );
  const negotiationDate =
    negotiation.type === "Offer"
      ? negotiation.dateReceived
      : negotiation.dateSent;
  const expirationDate =
    negotiation.type === "Offer" || negotiation.type === "Demand"
      ? negotiation.dateExpires
      : undefined;
  const details =
    negotiation.type === "Offer" || negotiation.type === "Demand"
      ? negotiation.details
      : undefined;
  const isMedPay =
    negotiation.type === "Offer"
      ? (negotiation.medPay ?? false)
      : negotiation.coverageType === "MedPay";
  const hasAmount = negotiation.amount !== null;
  return (
    <MatterSettlementNegotiationContextMenu
      matter={matter}
      negotiation={negotiation}
      onEdit={() => {
        onEdit();
      }}
      trigger={
        <Card className="w-full border-none">
          <CardHeader className="space-y-1 pb-2">
            <div className="flex items-center justify-between gap-y-2">
              <div className="flex flex-col gap-x-2">
                <div className="flex min-w-0 flex-1 items-center gap-x-2">
                  <Text
                    variant="body"
                    color="primary"
                    className="flex min-w-0 items-center gap-x-2 truncate font-semibold"
                  >
                    {negotiation.type === "Offer"
                      ? "Offer"
                      : negotiation.type === "Counter"
                        ? "Counter"
                        : "Demand"}
                    {hasAmount && (
                      <>
                        <span className="mx-0.5">|</span>
                        <span>
                          <CurrencyWrapper
                            value={negotiation.netSettlementAmount}
                          />
                        </span>
                      </>
                    )}
                    {((negotiation.type === "Offer" && negotiation.accepted) ||
                      (negotiation.type === "Counter" &&
                        negotiation.accepted) ||
                      (negotiation.type === "Demand" &&
                        negotiation.coverageType === "MedPay" &&
                        negotiation.accepted)) && (
                      <>
                        <Badge className="h-6">Accepted</Badge>
                        {negotiation.type === "Offer" &&
                          negotiation.acceptedDate && (
                            <Text variant="caption" color="muted" as="span">
                              Date accepted:{" "}
                              {formatDate(negotiation.acceptedDate)}
                            </Text>
                          )}
                        {negotiation.type === "Counter" &&
                          negotiation.acceptedDate && (
                            <Text variant="caption" color="muted" as="span">
                              Date accepted:{" "}
                              {formatDate(negotiation.acceptedDate)}
                            </Text>
                          )}
                        {negotiation.type === "Demand" &&
                          negotiation.acceptedDate && (
                            <Text variant="caption" color="muted" as="span">
                              Date accepted:{" "}
                              {formatDate(negotiation.acceptedDate)}
                            </Text>
                          )}
                      </>
                    )}
                    {acceptedStatus && (
                      <Badge variant="warning" className="h-6">
                        {acceptedStatus}
                      </Badge>
                    )}
                    {hasIssues && (
                      <Badge variant="destructive" className="h-6">
                        Issues
                      </Badge>
                    )}
                    {isMedPay && (
                      <Badge variant="outline" className="h-6">
                        MedPay
                      </Badge>
                    )}
                    {isPolicyLimit && (
                      <Badge variant="outline" className="h-6">
                        Policy limits
                      </Badge>
                    )}
                    {negotiation.trustChecks.length > 0 && (
                      <Badge variant="outline" className="h-6">
                        {pluralize(
                          "check",
                          negotiation.trustChecks.length,
                          true,
                        )}
                      </Badge>
                    )}
                  </Text>
                </div>
              </div>
              <div className="flex items-center gap-x-2">
                <Text
                  variant="subHeading"
                  color="primary"
                  className="whitespace-nowrap"
                >
                  {formatDate(negotiationDate)}
                </Text>
                {canOpenSettlementSlideover && (
                  <Button
                    type="button"
                    data-testid={`edit-${negotiation.type.toLowerCase()}-button`}
                    variant="ghost"
                    size="icon"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEdit();
                    }}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="mt-2">
              {expirationDate && (
                <Text variant="subHeading">
                  {dayjs(expirationDate).isAfter(dayjs())
                    ? "Expires: "
                    : "Expired: "}
                  {formatDate(expirationDate)}
                </Text>
              )}
              {details && (
                <ExpandableText
                  text={details}
                  maxLines={3}
                  className="text-muted-foreground text-sm"
                />
              )}
            </div>
          </CardContent>
        </Card>
      }
    />
  );
};
